import { DocumentMeta } from '../meta';
import {
  PaymentStatus,
  PaymentType,
  StatementType,
  StatementStatus,
  rightholderGroups,
  statementsRolesMapping,
  NegotiationStatus,
  RightType,
  Media,
  Territory,
  StatementMode,
  StatementDisplayMode,
  StatementView
} from '../static';
import {
  Version,
  Waterfall,
  WaterfallContract,
  WaterfallContractPriceReference,
  WaterfallEvent,
  WaterfallRightholder,
  WaterfallSource,
  WaterfallSourceLite,
  createWaterfallEvent,
  getIncomesSources,
  isStandaloneVersion
} from './waterfall';
import { dealTypes } from './utils';
import { Duration, createDuration } from '../terms';
import { sortByDate, sum, toFixed } from '../utils';
import { TitleState, TransferState } from './state';
import { PercentConfig, Right, RightGroups, RightOverride, createRightOverride, getChilds, getRightExpenseTypes, getRootGroup, skipGroups } from './right';
import { getPath, getSources, pathExists } from './node';
import { Income, getMatchingIncomes, IncomeLite, createIncomeLite } from '../income';
import { Expense, getExpenseTypes } from '../expense';
import { InterestDetail } from './interest';
import { TargetIn } from './conditions';
import { hasContractWith, getContractsWith } from '../contract';

export const areEqual = (a: number, b: number, epsilon = 0.00001) => Math.abs(a - b) < epsilon;
export const groupIdSeparator = '_group-id_';

// -----------------
// Interfaces
// -----------------

export interface Payment {
  id: string;
  type: PaymentType;
  price: number;
  date?: Date;
  status: PaymentStatus;
  mode: 'internal' | 'external';
}

/**
 * "To Income" payment to the rightholder's org of the statement, will increment org actual revenu and turnover
 */
export interface IncomePayment extends Payment {
  type: 'income';
  incomeId: string; // Income related to this payment
  status: 'received';
  mode: 'internal';
}

/**
 * "To Rightholder" payment to the org of the opposite rightholder of the statement (licensor or licensee)
 * Once received, will take money available in Org.revenu.actual and assign it to the other Org, incrementing org actual revenu and turnover
 */
export interface RightholderPayment extends Payment {
  type: 'rightholder';
  mode: 'external';
  incomeIds: string[]; // Incomes related to this payment
}

/**
 * "To Right" payment (com, expense, MG etc)
 * Once received, will assign money available in Org.revenu.actual to correct rights, incrementing right actual revenu and turnover
 */
export interface RightPayment extends Payment {
  type: 'right';
  mode: 'internal' | 'external';
  incomeIds: string[]; // Incomes related to this payment
  to: string; // rightId
  shadow: number; // Current shadow revenu for this right
}

export interface StatementPayments {
  income?: IncomePayment[];
  right: RightPayment[]
  rightholder?: RightholderPayment;
}

export interface StatementHiddenData {
  [key: string]: { id: string }[];
}

export interface Statement {
  _meta?: DocumentMeta;
  type: StatementType;
  mode: StatementMode;
  displayMode: StatementDisplayMode;
  contractId?: string;
  status: StatementStatus;
  reviewStatus?: NegotiationStatus;
  id: string;
  waterfallId: string;
  senderId: string, // rightholderId of statement creator
  receiverId: string, // rightholderId of statement receiver
  duration: Duration;
  reported?: Date;
  incomeIds: string[];
  expenseIds?: string[];
  versionId: string; // Version used to create this statement. Except for standalone versions, this id is set when statement is reported
  duplicatedFrom: string; // Id of the statement this one was duplicated from
  standalone: boolean; // True if statement was generated using a standalone waterfall version
  payments: StatementPayments;
  events?: WaterfallEvent[]; // Declared events only for Distributor and Direct Sales statements
  comment: string;
  rightOverrides: RightOverride[];
  reportedData: { // Final data of the statement once it is reported
    // For Distributor and Direct Sales statements.
    sourcesBreakdown?: SourcesBreakdown[];
    sourcesBreakdownIntermediary?: SourcesBreakdown[];
    grossReceiptsBreakdown?: SourcesBreakdown[];
    sourcesBreakdownSummary?: SourcesBreakdownSummary;
    distributorLimitedView?: DistributorLimitedView[];

    // For Outgoing statements
    outgoingBreakdown?: OutgoingBreakdown[];
    outgoingBreakdownIntermediary?: OutgoingBreakdown[];
    interests?: InterestDetail[]; // Interest details
    amortization?: RecoupmentsBreakdown; // Amortization details for outgoing statements
    dsmBreakdown?: DsmBreakdown[];
    outgoingLimitedView?: OutgoingLimitedView[];
    executiveSummary?: ExecutiveSummary[];

    // For DSM statements
    dsmIncomes?: IncomeBreakdown;

    // For All kinds of statements except DSM
    waterfallEvents?: WaterfallEvent[]; // Events declared on distributor statements, also copied to Outgoing statements
    recoupments?: RecoupmentsBreakdown[];
    expensesBreakdown?: ExpensesBreakdown[];
  },
  hiddenData?: StatementHiddenData;
  hash: {
    requested: boolean;
    requestDate?: Date;
    requestedBy?: string; // User uid
    hash?: string;
  }
}

export interface StatementWithNumber extends Statement {
  number: number;
}

export interface DistributorStatement extends Statement {
  type: 'mainDistributor' | 'salesAgent';
  contractId: string; // For distributor statements, rightholderId is licensee (and producer is licensor) of this contract
  expenseIds: string[];
  payments: {
    income: IncomePayment[];
    right: RightPayment[]; // Mode internal & external
    rightholder: RightholderPayment;
  };
  additional: {
    quantitySold?: number;
    salesContractIds?: {
      incomeId: string;
      contractId: string;
    }[];
  },
  events: WaterfallEvent[], // Declared events
}

export interface ProducerStatement extends Statement {
  type: 'producer';
  contractId: string; // For outgoing statements, rightholderId (producer) is licensor of this contract, except for statements made to author (in this case, producer is licensee)
  payments: {
    right: RightPayment[]; // Mode external
    rightholder: RightholderPayment;
  };
}

export interface DirectSalesStatement extends Statement {
  type: 'directSales';
  expenseIds: string[];
  payments: {
    income: IncomePayment[];
    right: RightPayment[]; // Mode internal
  };
  events: WaterfallEvent[], // Declared events
}

export interface DsmStatement extends Statement {
  type: 'dsm';
}

export interface MaxPerIncome {
  income: IncomeLite;
  max: number;
  current: number;
  source: WaterfallSourceLite;
}

export type BreakdownRowType =
  /* Income / Source related */
  'income' | // Row describing an income
  'extra' | // Income with extra
  'grossReceipts' | // Sum of incomes if more than one (used in statements and in incomes-table)
  'source' | // Row describing a source
  /* Right related*/
  'right' | // Single right
  'right-horizontal' | // Right inside a horizontal group
  'right-vertical' | // Right inside a vertical group
  'right-sub-vertical' | // Right inside a vertical group that is inside a horizontal group
  /* Group related */
  'horizontal' | // Horizontal group
  'vertical' | // Vertical group
  'sub-vertical' | // Vertical group inside a horizontal group
  /** Total related */
  'net' | // Total/Last row
  /** Event related */
  'ocurred-event' | // @see StatementEventsSummaryComponent
  /* Recoupment related */
  'original-amount' | // First line of recoupment table: contract, specific amount or expenses amount. Similar to "source"
  'recoupment-total' | // Sum of rights if more than one. Similar to "grossReceipts"
  'outstanding' | // What is left to recoup
  /* Expense related */
  'expense-header' | // Expense header
  'expense' | // Row describing an expense
  'expense-exceeding' | // Row describing an expense exceeding the cap
  'expense-total'; // Total of expenses

export const breakdownRowTypeCategories: Record<string, BreakdownRowType[]> = {
  withoutCurrency: ['extra', 'ocurred-event', 'expense-header'],
  number: ['extra', 'ocurred-event'],
  withoutRemaining: ['net', 'right-vertical', 'right-sub-vertical', 'right-horizontal', 'sub-vertical'],
  standard: [
    /* Income / Source related */
    'grossReceipts',
    'source',
    /* Right related*/
    'right',
    'right-horizontal',
    'right-vertical',
    'right-sub-vertical',
    /* Group related */
    'horizontal',
    'vertical',
    'sub-vertical',
    /** Total related */
    'net',
    /* Recoupment related */
    'original-amount',
    'recoupment-total',
    'outstanding',
    /* Expense related */
    'expense-header',
    'expense',
    'expense-exceeding',
    'expense-total'
  ],
  right: ['right', 'right-vertical', 'right-sub-vertical', 'right-horizontal'],
  withParenthesis: [
    /* Right related*/
    'right',
    'right-horizontal',
    'right-vertical',
    'right-sub-vertical',
    /* Group related */
    'horizontal',
    'vertical',
    'sub-vertical',
    /* Recoupment related */
    'recoupment-total',
    /* Expense related */
    'expense-exceeding'
  ]
};

export type BreakdownRowOrigin = 'internal' | 'external';

export interface BreakdownRow {
  id?: string;
  section: string;
  type?: BreakdownRowType;
  previous: number;
  current: number;
  cumulated: number;
  remaining?: number;
  position?: number; // Position in the tree from the source
  groupPosition?: string;
  rightId?: string;
  percentConfig?: PercentConfig;
  source?: WaterfallSourceLite;
  income?: IncomeLite;
  cap?: number; // For expenses table
  capped?: boolean; // For expenses table
  totalCapped?: number; // For expenses table
  expensesIds?: string[];
  maxPerIncome?: MaxPerIncome[]; // Used for right overrides
  medias?: Media[];
  territories?: Territory[];
  licensee?: string;
  contractPrice?: WaterfallContractPriceReference;
  css?: string; // Custom css for table row
  rowStyle?: 'even' | 'odd';
  origin?: BreakdownRowOrigin; // To adjust the styling of the row if it is an internal (statement) or an external right
}

export interface SourcesBreakdown {
  name: string;
  id: string;
  rows: BreakdownRow[];
  gross: number; // What is received from the source
  external: number; // What goes to the third parties before RNPP (external rights that are above or at same level as Distributor's entitlements)
  net: number; // What should be distributed to RNPP (financiers)
}

export interface SourcesBreakdownSummary {
  gross: number; // What is received from the sources
  producerShare: number; // What is paid by the distributor to the producer ( gross - distributor's entitlements deductions = producerShare = net + external)
  external: number; // What goes to the third parties before RNPP (external rights that are above or at same level as Distributor's entitlements)
  net: number; // What should be distributed to RNPP (financiers)
}

export interface DsmBreakdown {
  name: string;
  id: string;
  rows: BreakdownRow[];
  total: number;
}

export interface IncomeBreakdown {
  rows: BreakdownRow[];
  total: number;
}

export interface DistributorLimitedView {
  name: string;
  id?: string;
  rows: BreakdownRow[];
  net: number;
  active?: boolean;
}

export type OutgoingLimitedView = DistributorLimitedView;

export interface RecoupmentsBreakdown {
  name: string;
  id?: string;
  target: TargetIn | 'number';
  rows: BreakdownRow[];
  current: number;
  key?: string;
}

export interface ExpensesBreakdown {
  name: string;
  rows: BreakdownRow[];
  current: number;
}

export interface OutgoingBreakdown {
  group: WaterfallSourceLite;
  id?: string;
  rows: BreakdownRow[];
  net: number;
  active?: boolean; // Will be true if source have received money
}

export interface ExecutiveSummary {
  name: string;
  id?: string;
  rows: BreakdownRow[];
  total: number;
}

export interface StatementsUrlParams {
  rightholderId: string;
  type: StatementType;
  contractId: string;
  statementMode: StatementMode;
  statementView: StatementView;
}

// -----------------
// Creation functions
// -----------------

function createPaymentBase(params: Partial<Payment> = {}): Payment {
  return {
    id: '',
    type: 'rightholder',
    price: 0,
    status: 'pending',
    mode: 'internal',
    ...params,
  };
}

export function createIncomePayment(params: Partial<IncomePayment> = {}): IncomePayment {
  const payment = createPaymentBase(params);
  return {
    incomeId: '',
    ...payment,
    type: 'income',
    status: 'received',
    mode: 'internal',
  }
}

export function createRightholderPayment(params: Partial<RightholderPayment> = {}): RightholderPayment {
  const payment = createPaymentBase(params);
  return {
    ...payment,
    incomeIds: params.incomeIds || [],
    mode: 'external',
    type: 'rightholder',
  }
}

export function createRightPayment(params: Partial<RightPayment> = {}): RightPayment {
  const payment = createPaymentBase(params);
  return {
    to: '',
    shadow: 0,
    ...payment,
    incomeIds: params.incomeIds || [],
    type: 'right',
    mode: params.mode
  }
}

function createStatementBase(params: Partial<Statement> = {}): Statement {
  return {
    id: '',
    type: 'producer',
    mode: 'default',
    displayMode: 'detailed', // Default display mode
    status: 'draft',
    waterfallId: '',
    senderId: '',
    receiverId: '',
    incomeIds: params.incomeIds || [],
    versionId: '',
    duplicatedFrom: '',
    standalone: false,
    payments: {
      right: params.payments?.right ? params.payments.right.map(createRightPayment) : []
    },
    comment: params.comment || '',
    reportedData: {},
    hash: { requested: false },
    ...params,
    duration: createDuration(params?.duration),
    rightOverrides: params.rightOverrides ? params.rightOverrides.map(createRightOverride) : []
  };
}

export function createStatement(params: Partial<Statement>) {
  if (isDistributorStatement(params)) return createDistributorStatement(params);
  if (isProducerStatement(params)) return createProducerStatement(params);
  if (isDirectSalesStatement(params)) return createDirectSalesStatement(params);
  if (isDsmStatement(params)) return createDsmStatement(params);
}

export function isDistributorStatement(statement: Partial<Statement>): statement is DistributorStatement {
  return Object.keys(rightholderGroups.distributors).includes(statement.type);
}

export function isProducerStatement(statement: Partial<Statement>): statement is ProducerStatement {
  return statement.type === 'producer';
}

export function isDirectSalesStatement(statement: Partial<Statement>): statement is DirectSalesStatement {
  return statement.type === 'directSales';
}

export function isDsmStatement(statement: Partial<Statement>): statement is DsmStatement {
  return statement.type === 'dsm';
}

export function createDistributorStatement(params: Partial<DistributorStatement> = {}): DistributorStatement {
  const statement = createStatementBase(params);
  return {
    contractId: '',
    ...statement,
    payments: {
      income: params.payments?.income ? params.payments.income.map(createIncomePayment) : [],
      right: statement.payments.right,
      rightholder: params.payments?.rightholder || undefined
    },
    type: params.type || 'mainDistributor',
    expenseIds: params.expenseIds || [],
    additional: {},
    events: params.events?.length ? params.events.map(createWaterfallEvent) : []
  };
}

export function createProducerStatement(params: Partial<ProducerStatement> = {}): ProducerStatement {
  const statement = createStatementBase(params);
  return {
    contractId: '',
    ...statement,
    payments: {
      right: statement.payments.right,
      rightholder: params.payments?.rightholder || undefined
    },
    type: 'producer',
  };
}

export function createDirectSalesStatement(params: Partial<DirectSalesStatement> = {}): DirectSalesStatement {
  const statement = createStatementBase(params);
  return {
    ...statement,
    payments: {
      income: params.payments?.income ? params.payments.income.map(createIncomePayment) : [],
      right: statement.payments.right
    },
    type: 'directSales',
    expenseIds: params.expenseIds || [],
    events: params.events?.length ? params.events.map(createWaterfallEvent) : []
  };
}

export function createDsmStatement(params: Partial<DsmStatement> = {}): DsmStatement {
  const statement = createStatementBase(params);
  return { ...statement, type: 'dsm' };
}

export function createUrlParamsFromStatement(statement: Partial<Statement> = {}, statementView: StatementView = 'beneficiary'): StatementsUrlParams {
  return {
    rightholderId: statement?.type ? statement[rightholderKey(statement.type)] : '',
    type: statement?.type || 'mainDistributor',
    contractId: statement?.contractId || '',
    statementMode: statement?.mode || 'default',
    statementView
  };
}

// -----------------
// Utility functions
// -----------------

/**
 * Return the key used to identify the rightholder in the statement that is not the producer
 * @param type 
 * @returns 
 */
export function rightholderKey(type: StatementType) {
  return type === 'producer' ? 'receiverId' : 'senderId';
}

export function filterStatements(type: StatementType, mode: StatementMode = 'default', parties: string[], contractId = '', statements: Statement[]) {
  if (parties.length !== 2) return [];
  const isSender = (s: Statement) => s.senderId === parties[0] && s.receiverId === parties[1];
  const isReceiver = (s: Statement) => s.senderId === parties[1] && s.receiverId === parties[0];
  const filteredStatements = statements
    .filter(s => s.type === type && (isSender(s)) || isReceiver(s))
    .filter(s => (!s.mode && mode === 'default') || s.mode === mode); /** @dev if s.mode is not set, consider it as default (backward compatibility) **/
  if (!['directSales', 'dsm'].includes(type)) return filteredStatements.filter(s => s.contractId === contractId);
  return filteredStatements;
}

/**
 * Filter statements that are related to the rightholder (sender or receiver) 
 * and also the parent statements used to generate the rightholder statements (if any).
 * @param statements 
 * @param rightholderId 
 * @returns 
 */
export function filterRightholderStatements(_statements: Statement[], rightholder: WaterfallRightholder) {
  const isDistributor = rightholder.roles.some(role => statementsRolesMapping.mainDistributor.includes(role));
  const isSalesAgent = rightholder.roles.some(role => statementsRolesMapping.salesAgent.includes(role));
  const isDirectSales = rightholder.roles.some(role => statementsRolesMapping.directSales.includes(role));

  const statements = (isDistributor || isSalesAgent || isDirectSales) ? _statements : _statements.filter(s => s.status === 'reported');

  const rightholderStatements = statements.filter(s => [s.senderId, s.receiverId].includes(rightholder.id));
  const rightholderStatementsIds = rightholderStatements.map(s => s.id);
  const incomeIds = rightholderStatements.map(s => s.incomeIds).flat();
  const parentStatements = statements.filter(s => !rightholderStatementsIds.includes(s.id) && !isProducerStatement(s) && s.incomeIds.some(id => incomeIds.includes(id)));
  return [...rightholderStatements, ...parentStatements];
}

export function sortStatements<T extends Statement>(_statements: T[], reverse = true, sortByReported = false): (T & { number: number })[] {
  // Sort statements by reported date if sortByReported is true
  const statements = sortByReported ? sortByDate(_statements, 'reported') : _statements;
  // Sort statements by duration.to
  const sortedStatements = sortByDate(statements, 'duration.to').map((s, i) => ({ ...s, number: i + 1 }));
  return reverse ? sortedStatements.reverse() : sortedStatements;
}

export function getStatementNumber(current: Statement, statements: Statement[]) {
  const filteredStatements = filterStatements(current.type, current.mode, [current.senderId, current.receiverId], current.contractId, statements);
  const history = sortStatements(filteredStatements);
  return history.find(s => s.id === current.id)?.number || 1;
}

/**
 * Return statement history of current statement
 * @param current 
 * @param statements 
 * @param onlyPrevious 
 * @returns 
 */
export function statementHistory(current: Statement, statements: Statement[], onlyPrevious = false) {
  const filteredStatements = filterStatements(current.type, current.mode, [current.senderId, current.receiverId], current.contractId, statements);
  const sortedStatements = sortStatements(filteredStatements);
  if (onlyPrevious) {
    const number = sortedStatements.find(s => s.id === current.id)?.number;
    return sortedStatements.filter(s => s.number < number);
  } else {
    return sortedStatements;
  }
}

/**
 * Return statement history of current statement with its parents:
 * history is current statement history and parent statements history
 * @param current 
 * @param _statements 
 * @returns 
 */
export function statementHistoryWithParents(current: Statement, _statements: Statement[]) {
  const parentStatements = getParentStatements(_statements, current.incomeIds);
  const statements = [...statementHistory(current, _statements)];
  for (const parent of parentStatements) {
    const parentHistory = statementHistory(parent, _statements);
    for (const h of parentHistory) {
      if (!statements.find(s => s.id === h.id)) statements.push(h);
    }
  }
  return sortStatements(statements);
}

/**
 * Return income history of current statement
 * @param statement 
 * @param statements 
 * @param incomes 
 * @returns 
 */
export function incomeHistory(statement: Statement, statements: Statement[], incomes: Income[]) {
  const history = statementHistory(statement, statements, true);
  const uniqueIds = Array.from(new Set(history.map(s => s.incomeIds).flat()));
  return incomes.filter(i => uniqueIds.includes(i.id));
}

/**
 * Return expense history of current statement
 * @param statement 
 * @param statements 
 * @param expenses 
 * @returns 
 */
export function expenseHistory(statement: Statement, statements: Statement[], expenses: Expense[]) {
  const history = statementHistory(statement, statements, true);
  const uniqueIds = Array.from(new Set(history.map(s => s.expenseIds).flat()));
  return expenses.filter(e => uniqueIds.includes(e.id));
}

/**
 * Return the rights that should be used during statement creation
 * Will include rights of senderId and/or receiverId depending of the statement type.
 * If statement have a contractId, it will also be used to filter rights.
 * @param statement 
 * @param _rights 
 * @returns 
 */
export function getStatementRights(statement: Statement, _rights: Right[]) {
  const rights = skipGroups(_rights);

  if (isDistributorStatement(statement)) {
    return rights.filter(r => r.rightholderId === statement.receiverId || r.contractId === statement.contractId);
  } else if (isProducerStatement(statement)) {
    return rights.filter(r => r.rightholderId !== statement.senderId && r.contractId === statement.contractId);
  } else if (isDirectSalesStatement(statement)) {
    return rights.filter(r => r.rightholderId === statement.senderId);
  }
}

/**
 * Return the rights that should be displayed on the statement view
 * @param statement 
 * @param _rights 
 * @returns 
 */
export function getStatementRightsToDisplay(statement: Statement, _rights: Right[]) {
  const rights = getStatementRights(statement, _rights);
  if (isDistributorStatement(statement) || isDirectSalesStatement(statement)) return rights.filter(r => r.rightholderId === statement.senderId);
  if (isProducerStatement(statement)) return rights;
}

/**
 * Return statement's internal rights ordered by their position between each others
 * @param statement 
 * @param rights 
 * @param state 
 * @returns 
 */
export function getStatementInternalRights(statement: Statement, rights: Right[], state: TitleState) {
  const displayedRights = getStatementRightsToDisplay(statement, rights);
  return getOrderedRights(displayedRights, state, statement);
}

/**
 * Return rights that are, for a given source, in higher or same position than the statement rights.
 * 
 * @dev in case of direct sales statements, entitlements belonging to producer but not defined 
 * in getDisplayedRightTypes will be considered as external rights. Correct origin can still be determined 
 * by comparing ids with getStatementRightsToDisplay.
 * @param enabledRights 
 * @param internalRights 
 * @param state 
 * @param source 
 * @returns 
 */
function getStatementExternalRights(enabledRights: Right[], internalRights: Right[], state: TitleState, source: WaterfallSource) {
  const externalRights: Right[] = [];
  for (const internalRight of internalRights) {
    const path = getPath(internalRight.id, source.id, state);
    const ids = path.filter(id => ![...internalRights.map(r => r.id), source.id].includes(id));
    const rights = ids.map(id => enabledRights.find(r => r.id === id)).filter(r => !!r);
    for (const right of rights) {
      if (right.type === 'horizontal') {
        const groupMembers = enabledRights.filter(r => r.groupId === right.id);
        for (const member of groupMembers) {
          if (member.type !== 'vertical') {
            if (!externalRights.find(r => r.id === member.id) && !internalRights.find(r => r.id === member.id)) externalRights.push(member);
          } else {
            const subMembers = enabledRights.filter(r => r.groupId === member.id);
            for (const subMember of subMembers) {
              if (!externalRights.find(r => r.id === subMember.id) && !internalRights.find(r => r.id === subMember.id)) externalRights.push(subMember);
            }
          }
        }
      } else if (right.type === 'vertical') {
        const groupMembers = enabledRights.filter(r => r.groupId === right.id);
        for (const member of groupMembers) {
          if (!externalRights.find(r => r.id === member.id) && !internalRights.find(r => r.id === member.id)) externalRights.push(member);
        }
      } else if (!externalRights.find(r => r.id === right.id)) {
        externalRights.push(right);
      }
    }
  }

  return externalRights;
}

/**
 * Return the rights that are related to a given source
 * @param rights 
 * @param state 
 * @param source 
 * @returns 
 */
export function getSourceRights(rights: Right[], state: TitleState, source: WaterfallSource, excludeGroups = false) {
  const sourceRights = rights.filter(right => {
    const rightSources = getSources(state, right.id);
    return rightSources.some(r => r.id === source.id);
  });
  return excludeGroups ? skipGroups(sourceRights) : sourceRights;
}

/**
 * Return the rights that should be displayed on the source breakdown (distributor, direct sales & producer main table)
 * @param rights 
 * @param enabledRights 
 * @param state 
 * @param source 
 * @param params
 * @returns 
 */
export function getSourceBreakdownRights(rights: RightGroups, state: TitleState, source: WaterfallSource, params: { hideLowestBrothers?: boolean, hideInternalRights?: boolean } = {}) {
  const sourceInternalRights = getSourceRights(rights.internal, state, source);
  const externalRights = getStatementExternalRights(rights.enabled, sourceInternalRights, state, source);
  const orderedRights = getOrderedRights(sourceInternalRights, state, undefined, externalRights);
  const firstStep = params.hideLowestBrothers ? hideLowestBrothers(orderedRights, sourceInternalRights, rights.all) : orderedRights;
  return params.hideInternalRights ? firstStep.filter(r => !sourceInternalRights.find(i => i.id === r.id)) : firstStep;
}

/**
 * This functions hides brothers (members of the same root group) that are not necessary to display on outgoing statements.
 * Theses rights are the ones that have no children (rights below them) belonging to current rightholder.
 * @param orderedRights 
 * @param rightholderRights 
 * @param state 
 * @param source 
 * @returns 
 */
function hideLowestBrothers(orderedRights: Right[], rightholderRights: Right[], allRights: Right[]): Right[] {
  const rootGroupsToFilter: string[] = [];
  for (const right of rightholderRights) {
    const rootGroupId = getRootGroup(right, allRights).id;
    const childs = getChilds(rootGroupId, allRights);
    const childsOfRightholder = childs.filter(c => rightholderRights.find(r => r.id === c.id) && getRootGroup(c, allRights).id !== rootGroupId);

    if (childsOfRightholder.length === 0 && !rootGroupsToFilter.includes(rootGroupId)) {
      rootGroupsToFilter.push(rootGroupId);
    }
  }

  const filtered: Right[] = [];
  for (const right of orderedRights) {
    if (rightholderRights.find(r => r.id === right.id) || !rootGroupsToFilter.find(rootGroupId => rootGroupId === getRootGroup(right, allRights).id)) {
      filtered.push(right);
    }
  }
  return filtered;
}

/**
 * On direct sales statements, only display commission and expenses rights
 * This is needed to show only theses kind of rights since directSales statements does not have a contract Id.
 * @param statement 
 * @returns 
 */
function getDisplayedRightTypes(statement?: Statement): RightType[] {
  return !!statement && isDirectSalesStatement(statement) ? ['commission', 'expenses'] : [];
}

/**
 * Return a subset of rights ordered by their position between each others
 * @param rights (statement/internal rights most of the time)
 * @param state 
 * @param statement
 * @param externalRights
 * @returns 
 */
export function getOrderedRights(rights: Right[], state: TitleState, statement?: Statement, externalRights: Right[] = []) {
  const displayedRightTypes = getDisplayedRightTypes(statement);
  if (rights.length === 1 && externalRights.length === 0) {
    return rights.filter(right => displayedRightTypes.length ? displayedRightTypes.includes(right.type) : true);
  }
  const firstStep = [...rights, ...externalRights].sort((a, b) => pathExists(a.id, b.id, state) ? 1 : -1);

  let secondStep = firstStep.filter(right => (displayedRightTypes.length && rights.find(r => r.id === right.id)) ? displayedRightTypes.includes(right.type) : true);

  // Sort group members together
  const thirdStep: Right[] = [];
  for (const right of secondStep) {
    if (right.groupId) {
      const brothers = secondStep.filter(r => r.groupId === right.groupId).sort((a, b) => a.order - b.order);
      thirdStep.push(...brothers);
      secondStep = secondStep.filter(r => r.groupId !== right.groupId);
      for (const brother of brothers) {
        if (brother.type === 'vertical') {
          const subBrothers = secondStep.filter(r => r.groupId === brother.id).sort((a, b) => a.order - b.order);
          thirdStep.push(...subBrothers);
          secondStep = secondStep.filter(r => r.groupId !== brother.id);
        }
      }
    } else {
      thirdStep.push(right);
    }
  }

  return thirdStep;
}

/**
 * Look into transfer state to find the history transfers for this rightId and incomeIds
 * @param rightId 
 * @param _incomeIds 
 * @param transferState 
 */
function getTransfersHistory(rightId: string, _incomeIds: string[] | string, transferState: Record<string, TransferState>, options: { checked: boolean } = { checked: true }) {
  const incomeIds = Array.isArray(_incomeIds) ? _incomeIds : [_incomeIds];
  const transfers = Object.values(transferState).filter(t => t.to === rightId);
  const history = transfers.map(t => t.history.filter(h => incomeIds.includes(h.incomeId))).flat();
  return options.checked ? history.filter(h => h.checked) : history;
}

/**
 * Look into transfer state to find the transfered amount (turnover) to this rightId for this incomeIds
 * @param rightId 
 * @param _incomeIds 
 * @param transferState 
 * @returns number
 */
export function getIncomingAmount(rightId: string, incomeIds: string[] | string, transferState: Record<string, TransferState>): number {
  const history = getTransfersHistory(rightId, incomeIds, transferState, { checked: false });
  return toFixed(sum(history, i => i.amount));
}

/**
 * Look into transfer state to find the calculated amount for this rightId and incomeIds
 * @param rightId 
 * @param _incomeIds 
 * @param transferState 
 * @returns number
 */
export function getCalculatedAmount(rightId: string, incomeIds: string[] | string, transferState: Record<string, TransferState>, options?: { rounded: boolean }): number {
  const history = getTransfersHistory(rightId, incomeIds, transferState);
  const amount = sum(history, i => i.amount * i.percent);
  return options?.rounded ? toFixed(amount) : amount;
}

function getStatementExpenseTypeIds(statement: Statement, _rights: Right[]) {
  /** 
   * @dev if needed to display expenses form as soon as a right have a condition pointing to an expense type and
   * not only on current statement rights, remove this line.
   */
  const rights = getStatementRightsToDisplay(statement, _rights);
  const expenseTypeIds: string[] = [];
  for (const right of rights) {
    const rightExpenseTypeIds = getRightExpenseTypes(right);
    for (const typeId of rightExpenseTypeIds) {
      if (!expenseTypeIds.includes(typeId)) expenseTypeIds.push(typeId);
    }
  }
  return expenseTypeIds;
}

/**
 * Return expense types defined in current rights conditions for a statement
 * @param statement 
 * @param rights 
 * @param waterfall 
 * @returns 
 */
export function getStatementExpenseTypes(statement: Statement, rights: Right[], waterfall: Waterfall) {
  const expenseTypeIds = getStatementExpenseTypeIds(statement, rights);
  return getExpenseTypes(statement.contractId, waterfall).filter(e => expenseTypeIds.includes(e.id));
}

export function hasRightsWithExpenseCondition(rights: Right[] = [], statement: Statement, waterfall: Waterfall) {
  return getStatementExpenseTypes(statement, rights, waterfall).length > 0;
}

export function convertStatementsTo(_statements: Statement[], version: Version) {
  if (!version?.id) return _statements;
  if (version.standalone) return _statements.filter(s => s.versionId === version.id);
  const statements = _statements.filter(s => !s.standalone);
  const duplicatedStatements = statements.filter(s => !!s.duplicatedFrom);
  const rootStatements = statements.filter(s => !s.duplicatedFrom);
  return rootStatements.map(s => duplicatedStatements.find(d => d.duplicatedFrom === s.id && d.versionId === version.id) || s);
}

export function getParentStatements(statements: Statement[], incomeIds: string[], skipDuplicates = false) {
  return statements.filter(s => isDirectSalesStatement(s) || isDistributorStatement(s))
    .filter(s => skipDuplicates ? !s.duplicatedFrom : true) // Skip already duplicated statements
    .filter(s => s.payments.income.some(p => incomeIds.includes(p.incomeId)));
}

export function getNonEditableNodeIds(rights: Right[], sources: WaterfallSource[], reportedStatements: Statement[], incomes: Income[]) {
  const incomeIds = Array.from(new Set(reportedStatements.map(s => s.incomeIds).flat()));
  const reportedIncomes = incomeIds.map(id => incomes.find(i => i.id === id)).filter(i => !!i);
  const nonEditableSources = reportedIncomes ? getIncomesSources(reportedIncomes, sources).filter(s => !!s) : [];
  const topLevelRights = nonEditableSources.map(s => rights.find(r => r.id === s.destinationId)).filter(r => !!r);
  const childIds = Array.from(new Set(topLevelRights.map(r => getChilds(r.id, rights).map(c => c.id)).flat()));
  const nonEditableRights = childIds.map(id => rights.find(r => r.id === id));
  const groupIds = Array.from(new Set(nonEditableRights.filter(r => r.groupId).map(r => r.groupId)));
  const nonEditableGroups = groupIds.map(id => rights.find(r => r.id === id));
  const parentGroupIds = Array.from(new Set(nonEditableGroups.filter(r => r.groupId).map(r => r.groupId))).filter(i => !!i);
  return [...nonEditableSources.map(s => s.id), ...nonEditableRights.map(r => r.id), ...nonEditableGroups.map(g => g.id), ...parentGroupIds];
}

export interface BreakdownRowWithActions extends BreakdownRow {
  actions?: boolean;
  preventDelete?: boolean;
  index?: number;
  contractId?: string;
}

export function incomeHistoryWithExtra(incomes: Income[], history: Income[], contracts: WaterfallContract[], includeTerritoriesAndMedias = false) {

  const data: BreakdownRowWithActions[] = [];
  let index = 0;
  for (const income of incomes) {

    // Find previous incomes with same contractId, media and territories
    const matchingIncomes = getMatchingIncomes(income, history);
    const previous = matchingIncomes.reduce((acc, m) => acc + m.price, 0);

    const row: BreakdownRowWithActions = {
      section: income.id,
      index: index++,
      contractId: income.contractId,
      actions: true,
      preventDelete: matchingIncomes.length !== 0,
      income: createIncomeLite(income),
      type: 'income',
      previous,
      current: income.price,
      cumulated: income.price + previous,
      licensee: income.licensee
    };

    if (includeTerritoriesAndMedias) {
      row.territories = income.territories;
      row.medias = income.medias;
    }

    const deal = contracts.find(d => d.id === income.contractId);
    if (deal && dealTypes.includes(deal.type)) {
      const price = deal.price;
      if (price.reference?.value > 0) {
        row.contractPrice = { currency: price.reference.currency, value: price.reference.value };
      } else {
        row.contractPrice = { value: price.value || 0 };
      }
    }

    data.push(row);

    for (const key of Object.keys(income.extra || {}).sort((a, b) => a.localeCompare(b))) {
      const previousExtra = matchingIncomes.reduce((acc, m) => acc + (m.extra && m.extra[key] ? m.extra[key] : 0), 0);
      data.push({
        section: '',
        type: 'extra',
        index: row.index,
        previous: previousExtra,
        current: income.extra[key],
        cumulated: income.extra[key] + previousExtra,
        licensee: key
      });
    }
  }

  /** 
   * Create row for each history without a current
   * @dev this should happen only when editing income-form 
   */
  const previousAdded: Income[] = [];
  for (const previous of history) {
    const matchingCurrent = getMatchingIncomes(previous, [...incomes, ...previousAdded]);
    const matchingHistory = getMatchingIncomes(previous, history);
    if (matchingCurrent.length === 0) {
      previousAdded.push(previous);
      const row: BreakdownRowWithActions = {
        section: previous.id,
        index: index++,
        contractId: previous.contractId,
        actions: true,
        preventDelete: true,
        income: createIncomeLite(previous),
        type: 'income',
        previous: sum(matchingHistory, e => e.price),
        current: 0,
        cumulated: sum(matchingHistory, e => e.price),
        licensee: previous.licensee
      };

      if (includeTerritoriesAndMedias) {
        row.territories = previous.territories;
        row.medias = previous.medias;
      }

      const deal = contracts.find(d => d.id === previous.contractId);
      if (deal && dealTypes.includes(deal.type)) {
        const price = deal.price;
        if (price.reference?.value > 0) {
          row.contractPrice = { currency: price.reference.currency, value: price.reference.value };
        } else {
          row.contractPrice = { value: price.value || 0 };
        }
      }

      data.push(row);

      for (const key of Object.keys(previous.extra || {}).sort((a, b) => a.localeCompare(b))) {
        data.push({
          section: '',
          type: 'extra',
          index: row.index,
          previous: sum(matchingHistory, e => e.extra[key]),
          current: 0,
          cumulated: sum(matchingHistory, e => e.extra[key]),
          licensee: key
        });
      }
    }
  }

  return data;
}

export function findRelatedStatements(statement: Statement, _statements: Statement[]): StatementWithNumber[] {
  const statements = _statements.filter(s => !s.duplicatedFrom);

  if (statement.status === 'draft') {
    const current = statements.find(s => s.id === statement.id);
    return [{ ...current, number: getStatementNumber(current, statements) }];
  }
  const notDrafts = statements.filter(s => s.status !== 'draft');

  const childs = isProducerStatement(statement) ? [] : notDrafts.filter(s => statement.incomeIds.some(id => s.incomeIds.includes(id)));
  const next = isProducerStatement(statement) ?
    notDrafts.filter(s => s.duration.to.getTime() > statement.duration.to.getTime()) :
    notDrafts.filter(s => {
      if (s.duration.to.getTime() != statement.duration.to.getTime()) {
        return s.duration.to.getTime() > statement.duration.to.getTime();
      }
      return s.reported?.getTime() > statement.reported?.getTime();
    });

  const ids = [...childs, ...next].map(s => s.id);
  ids.push(statement.id);
  const uniqueIds = Array.from(new Set(ids));
  return notDrafts.filter(s => uniqueIds.includes(s.id)).map(s => ({ ...s, number: getStatementNumber(s, statements) }));
}

/**
 * Return true if there already are statements for the given type, rightholder and contract
 * @param type 
 * @param rightholderId 
 * @param contractId 
 * @param statements 
 * @returns 
 */
export function statementsExists(type: StatementType, rightholderId: string, contractId: string, statements: Statement[]) {
  return statements.some(stm => stm[rightholderKey(type)] === rightholderId && stm.contractId === contractId);
}

/**
 * 
 * @param waterfall 
 * @param producerId 
 * @param type 
 * @param currentStateDate 
 * @param _contracts 
 * @param statements 
 * @param canBypassRules 
 * @param currentRightholderId 
 * @param versionId 
 * @returns 
 */
export function getFirstStatementBeneficiaries(
  waterfall: Waterfall,
  producerId: string,
  type: StatementType,
  currentStateDate: Date,
  _contracts: WaterfallContract[],
  statements: Statement[],
  canBypassRules: boolean,
  currentRightholderId: string,
  versionId: string
) {
  return waterfall.rightholders
    .filter(r => r.id !== producerId)
    .filter(r => r.roles.some(role => statementsRolesMapping[type].includes(role)))
    .filter(r => {
      const contracts = getContractsWith([producerId, r.id], _contracts, currentStateDate)
        .filter(c => statementsRolesMapping[type].includes(c.type));
      if (!contracts.length) return false;

      const isMatchingVersion = isRightholderMatchingVersion(r, waterfall, versionId, type);
      if (!isMatchingVersion) return false;

      // If there is at least one contract that does not have statement, we display rightholder
      return contracts.some(c => !statementsExists(type, r.id, c.id, statements));
    })
    .filter(r => canBypassRules || r.id === currentRightholderId);
}

/**
 * @dev this function is similar to getFirstStatementBeneficiaries but specific to outgoing statements
 * and with less restrictions
 * @param waterfall 
 * @param producerId 
 * @param currentStateDate 
 * @param contracts 
 * @param canBypassRules 
 * @param currentRightholderId 
 * @param versionId 
 * @returns 
 */
export function getOutgoingStatementBeneficiaries(
  waterfall: Waterfall,
  producerId: string,
  currentStateDate: Date,
  contracts: WaterfallContract[],
  canBypassRules: boolean,
  currentRightholderId?: string,
  versionId?: string) {
  return waterfall.rightholders
    .filter(r => r.id !== producerId)
    .filter(r => hasContractWith([producerId, r.id], contracts, currentStateDate)) // Rightholder have a contract with the statement sender (the producer)
    .filter(r => r.roles.some(role => statementsRolesMapping.producer.includes(role))) // Rightholder can receive an outgoing statement
    .filter(r => !versionId || isRightholderMatchingVersion(r, waterfall, versionId, 'producer'))
    .filter(r => canBypassRules || (!!currentRightholderId && r.id === currentRightholderId));
}

function isRightholderMatchingVersion(rightholder: WaterfallRightholder, waterfall: Waterfall, versionId: string, type: StatementType) {
  const isStandalone = isStandaloneVersion(waterfall, versionId);
  if (isStandalone) {
    if (type === 'producer' && rightholder.lockedVersionId !== versionId) return false;
  } else {
    const isLockedStandalone = isStandaloneVersion(waterfall, rightholder.lockedVersionId);
    if (isLockedStandalone) return false;
  }
  return true;
}

export function getStatementInitialVersion(waterfall: Waterfall, statement: Statement) {
  const rightholder = waterfall.rightholders.find(r => r.id === statement[rightholderKey(statement.type)]);

  // Set version to the one of the statement if any and unless a locked version is found for an outgoing statement
  let initialVersion = statement.versionId;
  const hasLockedVersion = rightholder.lockedVersionId && waterfall.versions.some(v => v.id === rightholder.lockedVersionId);
  if (isProducerStatement(statement) && hasLockedVersion && statement.status !== 'reported') initialVersion = rightholder.lockedVersionId; // TODO #10457

  return initialVersion;
}

export function resetPayments(statement: Statement) {
  if (statement.status !== 'draft') return statement;

  statement.payments.right = [];
  if (isDistributorStatement(statement) || isDirectSalesStatement(statement)) {
    statement.payments.income = [];
  }
  if (isDistributorStatement(statement) || isProducerStatement(statement)) {
    delete statement.payments.rightholder;
  }

  return statement;
}